import React, { useEffect, useRef, useState } from 'react';
import {
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Box,
  Fade,
  IconButton,
  Paper,
  Slide,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

// Liquid glass tuned for light surfaces: navy gradient + readable contrast
const FloatingChatButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  width: 60,
  height: 60,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.primary.main, 0.92)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.88)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
  borderRadius: '50%',
  boxShadow: `
    0 8px 28px ${alpha(theme.palette.primary.main, 0.35)},
    inset 0 1px 0 ${alpha(theme.palette.common.white, 0.18)}
  `,
  color: theme.palette.primary.contrastText,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 1000,

  '&:hover': {
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.primary.main, 0.98)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.94)} 100%)`,
    transform: 'scale(1.1) rotate(5deg)',
    boxShadow: `
      0 12px 36px ${alpha(theme.palette.primary.main, 0.45)},
      inset 0 1px 0 ${alpha(theme.palette.common.white, 0.25)}
    `,
  },

  '&:active': {
    transform: 'scale(0.95) rotate(0deg)',
  },

  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: 56,
    height: 56,
  },
}));

const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  width: 380,
  maxHeight: 600,
  background: `linear-gradient(135deg, 
    rgba(2, 5, 18, 0.95) 0%, 
    rgba(7, 5, 14, 0.9) 100%)`,
  backdropFilter: 'blur(30px)',
  border: `1px solid rgba(4, 58, 147, 0.2)`,
  borderRadius: 24,
  boxShadow: `
    0 25px 80px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    0 0 0 1px rgba(4, 58, 147, 0.1)
  `,
  overflow: 'hidden',
  zIndex: 1000,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    boxShadow: `
      0 30px 100px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      0 0 0 1px rgba(4, 58, 147, 0.15)
    `,
  },

  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    left: theme.spacing(2),
    width: 'auto',
    maxHeight: '70vh',
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: `linear-gradient(135deg, 
    rgba(4, 58, 147, 0.2) 0%, 
    rgba(125, 102, 217, 0.15) 100%)`,
  backdropFilter: 'blur(25px)',
  borderBottom: `1px solid rgba(4, 58, 147, 0.2)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: `
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3)
  `,
  color: 'rgba(255, 255, 255, 0.96)',
  '& .MuiTypography-root': {
    color: 'inherit',
  },
  '& .MuiIconButton-root': {
    color: 'rgba(255, 255, 255, 0.88)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  maxHeight: 400,
  overflowY: 'auto',
  background: `linear-gradient(135deg, 
    rgba(2, 5, 18, 0.4) 0%, 
    rgba(7, 5, 14, 0.2) 100%)`,
  backdropFilter: 'blur(15px)',

  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(4, 58, 147, 0.1)',
    borderRadius: 4,
    margin: theme.spacing(1),
  },
  '&::-webkit-scrollbar-thumb': {
    background: `linear-gradient(135deg, 
      rgba(4, 58, 147, 0.5) 0%, 
      rgba(125, 102, 217, 0.3) 100%)`,
    borderRadius: 4,
    border: `1px solid rgba(4, 58, 147, 0.2)`,
    '&:hover': {
      background: `linear-gradient(135deg, 
        rgba(4, 58, 147, 0.7) 0%, 
        rgba(125, 102, 217, 0.5) 100%)`,
    },
  },
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',

  '& .message-content': {
    maxWidth: '80%',
    padding: theme.spacing(2, 3),
    borderRadius: 24,
    background: isUser
      ? `linear-gradient(135deg, 
          rgba(4, 58, 147, 0.25) 0%, 
          rgba(4, 58, 147, 0.15) 100%)`
      : `linear-gradient(135deg, 
          rgba(125, 102, 217, 0.2) 0%, 
          rgba(125, 102, 217, 0.1) 100%)`,
    backdropFilter: 'blur(25px)',
    border: `1px solid ${
      isUser ? 'rgba(4, 58, 147, 0.4)' : 'rgba(125, 102, 217, 0.3)'
    }`,
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.4)
    `,
    color: 'rgba(255, 255, 255, 0.96)',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    // MUI Typography defaults to theme text.primary (dark); force readable copy on glass bubbles
    '& .MuiTypography-root': {
      color: 'inherit',
    },

    '&:hover': {
      boxShadow: `
        0 12px 40px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.5)
      `,
      transform: 'translateY(-2px)',
    },
  },
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid rgba(4, 58, 147, 0.15)`,
  background: `linear-gradient(135deg, 
    rgba(2, 5, 18, 0.9) 0%, 
    rgba(7, 5, 14, 0.8) 100%)`,
  backdropFilter: 'blur(20px)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),

  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(2, 3),
    background: `linear-gradient(135deg, 
      rgba(2, 5, 18, 0.98) 0%, 
      rgba(7, 5, 14, 0.95) 100%)`,
    backdropFilter: 'blur(30px)',
    borderTop: `1px solid rgba(4, 58, 147, 0.25)`,
    boxShadow: `
      0 -8px 32px rgba(0, 0, 0, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.4)
    `,
    zIndex: 1001,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: `linear-gradient(135deg, 
      rgba(2, 5, 18, 0.9) 0%, 
      rgba(7, 5, 14, 0.8) 100%)`,
    backdropFilter: 'blur(25px)',
    borderRadius: 30,
    border: `1px solid rgba(4, 58, 147, 0.25)`,
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,

    '&:hover': {
      borderColor: 'rgba(4, 58, 147, 0.5)',
      boxShadow: `
        0 12px 40px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.4)
      `,
    },

    '&.Mui-focused': {
      borderColor: '#043a93',
      boxShadow: `
        0 0 0 2px rgba(4, 58, 147, 0.4),
        0 16px 48px rgba(0, 0, 0, 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.5)
      `,
    },
  },

  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(2, 3),
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.98)',
    WebkitTextFillColor: 'rgba(255, 255, 255, 0.98)',

    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
      WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)',
    },
  },

  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(255, 255, 255, 0.42)',
    color: 'rgba(255, 255, 255, 0.42)',
  },

  '& .MuiOutlinedInput-input:-webkit-autofill': {
    WebkitTextFillColor: 'rgba(255, 255, 255, 0.98) !important',
    caretColor: 'rgba(255, 255, 255, 0.95)',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const HouseSubmitButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    #043a93 0%, 
    #032a6b 100%)`,
  color: '#ffffff',
  width: 44,
  height: 44,
  borderRadius: '50%',
  boxShadow: `
    0 6px 20px rgba(4, 58, 147, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3)
  `,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid rgba(4, 58, 147, 0.4)`,

  '&:hover': {
    background: `linear-gradient(135deg, 
      #032a6b 0%, 
      #043a93 100%)`,
    transform: 'scale(1.1) rotate(5deg)',
    boxShadow: `
      0 8px 25px rgba(4, 58, 147, 0.7),
      inset 0 1px 0 rgba(255, 255, 255, 0.4)
    `,
  },

  '&:active': {
    transform: 'scale(0.95) rotate(0deg)',
  },

  '&:disabled': {
    background: 'rgba(176, 176, 176, 0.3)',
    transform: 'scale(1)',
    boxShadow: 'none',
  },
}));

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface AIChatBubbleProps {
  onClose?: () => void;
}

export const AIChatBubble: React.FC<AIChatBubbleProps> = ({ onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hi! I'm your AI assistant. How can I help you with your loan today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Simulate AI response (replace with actual API call)
    setTimeout(() => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I understand you're asking about that. Let me help you with more specific information about your loan options.",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile: Sticky bottom button */}
        <FloatingChatButton
          onClick={handleToggleChat}
          sx={{
            position: 'fixed',
            bottom: theme.spacing(2),
            right: theme.spacing(2),
            left: 'auto',
          }}
        >
          <ChatIcon />
        </FloatingChatButton>

        {/* Mobile: Full-width chat overlay */}
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
          <ChatContainer
            sx={{
              bottom: 0,
              right: 0,
              left: 0,
              width: '100%',
              maxHeight: '70vh',
              borderRadius: '20px 20px 0 0',
            }}
          >
            <ChatHeader>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                AI Loan Officer & Real Estate Specialist
              </Typography>
              <IconButton onClick={handleCloseChat} size="small">
                <CloseIcon />
              </IconButton>
            </ChatHeader>

            <ChatMessages
              sx={{
                maxHeight: 'calc(70vh - 140px)', // Account for fixed input
                pb: 8, // Add padding bottom for fixed input
              }}
            >
              {messages.map((message) => (
                <MessageBubble key={message.id} isUser={message.isUser}>
                  <Box className="message-content">
                    <Typography variant="body2">{message.text}</Typography>
                  </Box>
                </MessageBubble>
              ))}
              {isLoading && (
                <MessageBubble isUser={false}>
                  <Box className="message-content">
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Typing...
                    </Typography>
                  </Box>
                </MessageBubble>
              )}
              <div ref={messagesEndRef} />
            </ChatMessages>
          </ChatContainer>
        </Slide>

        {/* Mobile: Fixed input field */}
        {isOpen && (
          <ChatInput>
            <StyledTextField
              fullWidth
              placeholder="Ask me anything about real estate and mortgages…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              size="small"
            />
            <HouseSubmitButton
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
              size="small"
            >
              <HomeIcon sx={{ fontSize: 20 }} />
            </HouseSubmitButton>
          </ChatInput>
        )}
      </>
    );
  }

  return (
    <>
      {/* Desktop: Floating button */}
      <FloatingChatButton onClick={handleToggleChat}>
        <ChatIcon />
      </FloatingChatButton>

      {/* Desktop: Floating chat window */}
      <Fade in={isOpen} mountOnEnter unmountOnExit>
        <ChatContainer>
          <ChatHeader>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              AI Loan Officer & Real Estate Specialist
            </Typography>
            <IconButton onClick={handleCloseChat} size="small">
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <ChatMessages>
            {messages.map((message) => (
              <MessageBubble key={message.id} isUser={message.isUser}>
                <Box className="message-content">
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
              </MessageBubble>
            ))}
            {isLoading && (
              <MessageBubble isUser={false}>
                <Box className="message-content">
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Typing...
                  </Typography>
                </Box>
              </MessageBubble>
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <ChatInput>
            <StyledTextField
              fullWidth
              placeholder="Ask me anything about real estate and mortgages…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              size="small"
            />
            <HouseSubmitButton
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
              size="small"
            >
              <HomeIcon sx={{ fontSize: 20 }} />
            </HouseSubmitButton>
          </ChatInput>
        </ChatContainer>
      </Fade>
    </>
  );
};
